import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormattedMessage, useIntl } from "react-intl";
import classes from "./iFame.module.scss";
import { a, useSpring } from "react-spring";
import { downloadCalendarEvent } from "../../../functions/classes/CalendarEvent.class.js";
import RoundedIcon from "../buttons/roundedIcon.component.jsx";
import { openURL } from "../../nativeAppInterface/NativeAppInterface.jsx";
import { MISC_INFO } from "../../../reduxStore/actions/main";

export default function IFrame({
  url,
  onClose,
  title,
  event,
  isTerminal = false,
  contentType,
  isApp = false,
}) {
  const [show, setShow] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [copyToClipboardIsAllowed, setCopyToClipboardIsAllowed] =
    useState(true);
  const intl = useIntl();
  const [springProps, springAPI] = useSpring(() => ({
    config: {
      tension: 200,
      friction: 22,
    },
    from: {
      opacity: 0,
      transform: "translateY(10rem)",
    },
  }));

  useEffect(() => {
    if (isApp) {
      openURL(url);
      handleClose();
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: "clipboard-write",
        });

        if (
          !(
            permissionStatus.state === "granted" ||
            permissionStatus.state === "prompt"
          )
        ) {
          setCopyToClipboardIsAllowed(false);
        }
      } catch (error) {
        setCopyToClipboardIsAllowed(false);
      }
    })();
  }, []);

  useEffect(() => {
    // wenn der Link ein MailLink ist -> schließen!
    if (url && url.includes("mailto:")) {
      handleClose();
    }
    // sometimes some pages dont allow an iframe, so a new tab has to be forced
    const forceNewTab =
      (contentType === "news" && MISC_INFO.openNewsInNewTab) ||
      (contentType === "event" && MISC_INFO.openEventsInNewTab) ||
      (contentType === "commonUrl" && MISC_INFO.openCommonUrlsInNewTab);

    if (forceNewTab && !isApp) {
      window.open(url, "_blank").focus();
      handleClose();
    }

    setTimeout(() => {
      try {
        try {
          let msges = ["didOpenInAppBrowser"];

          msges.forEach((msg) => {
            window.webkit.messageHandlers.callbackHandler.postMessage(msg);
          });
        } catch (e) {}
      } catch (e) {
        //do nothing
      }
    });
    setTimeout(() => {
      springAPI.start({
        immediate: false,
        opacity: 1,
        transform: "translateY(0rem)",
      });
    }, 250);

    return () => {
      try {
        try {
          let msges = ["didCloseInAppBrowser"];

          msges.forEach((msg) => {
            window.webkit.messageHandlers.callbackHandler.postMessage(msg);
          });
        } catch (e) {}
      } catch (e) {
        //do nothing
      }
    };
  }, []);

  const handleClose = () => {
    springAPI.start({
      immediate: false,
      opacity: 0,
      transform: "translateY(10rem)",
      onRest: onClose,
    });
    setTimeout(() => {
      onClose();
    }, 350);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setShow(false);
  };

  const handleOpenInBrowser = () => {
    openURL(url);
  };

  const handleShowShare = () => {
    setShow(false);
    const shareData = {
      text: title,
      url: url,
    };
    navigator.share(shareData);
  };

  const handleReload = () => {
    // Keine Ahnung warum ich nicht die src neu setzten kann. Lösung von stackoverflow: https://stackoverflow.com/questions/48830316/how-do-you-reload-an-iframe-with-reactjs
    setReloadKey((prevState) => prevState + 1);
    setShow(false);
  };
  let shareAllowed = false;

  if (navigator.share) {
    shareAllowed = true;
  }

  return (
    <div className={`${classes.main} pointer-event-on`}>
      <div onClick={handleClose} className={classes.shadow} />
      <a.div style={springProps} className="h-100 d-flex flex-column">
        <header className={`d-flex general-padding ${classes.header}`}>
          <RoundedIcon
            onClick={handleClose}
            divClass={"roundedIcon__iFrame"}
            icon={["fal", "times"]}
          />
          <span
            className={`general-margin-horizontally general-padding rounded-5 ${classes.url}`}
          >
            {url}
          </span>
          <RoundedIcon
            divClass={`${show ? classes.activeButton : ""}`}
            icon={["fal", "ellipsis-h"]}
            onClick={() => setShow((prevState) => !prevState)}
          />

          {show ? (
            <div className={classes.navigation}>
              {!isTerminal ? (
                !isApp ? (
                  <a href={url} target="_blank" className="btn">
                    <FontAwesomeIcon icon={["fal", "browser"]} />
                    <span>
                      <FormattedMessage id="iframe.openInBrowser" />
                    </span>
                  </a>
                ) : (
                  <button className="btn" onClick={handleOpenInBrowser}>
                    <FontAwesomeIcon icon={["fal", "browser"]} />
                    <span>
                      <FormattedMessage id="iframe.openInBrowser" />
                    </span>
                  </button>
                )
              ) : null}
              {copyToClipboardIsAllowed && (
                <>
                  {!isTerminal ? (
                    <button className="btn" onClick={handleCopy}>
                      <FontAwesomeIcon icon={["fal", "copy"]} />
                      <span>
                        <FormattedMessage id="iframe.copyLink" />
                      </span>
                    </button>
                  ) : null}
                  {shareAllowed ? (
                    <button className="btn" onClick={handleShowShare}>
                      <FontAwesomeIcon icon={["fal", "share"]} />
                      <span>
                        <FormattedMessage id="iframe.share" />
                      </span>
                    </button>
                  ) : null}
                </>
              )}
              {event && !isApp ? (
                <button
                  className="btn"
                  onClick={(e) =>
                    downloadCalendarEvent(
                      event,
                      `${intl.formatMessage({
                        id: "iframe.eventName",
                      })}.ics`
                    )
                  }
                >
                  <FontAwesomeIcon icon={["fal", "calendar-plus"]} />
                  <span>
                    <FormattedMessage id="iframe.event" />
                  </span>
                </button>
              ) : null}
              <button className="btn" onClick={handleReload}>
                <FontAwesomeIcon icon={["fal", "sync-alt"]} />
                <span>
                  <FormattedMessage id="iframe.reload" />
                </span>
              </button>
            </div>
          ) : null}
        </header>
        <section className={`${classes.section} h-100 d-flex flex-column`}>
          <div className={classes.loading}>
            <svg
              id="enqkp4e94b31"
              viewBox="0 0 70 70"
              shapeRendering="geometricPrecision"
              textRendering="geometricPrecision"
            >
              <g id="enqkp4e94b32">
                <g id="enqkp4e94b33">
                  <g
                    id="enqkp4e94b34_to"
                    transform="translate(34.998677,21.709246)"
                  >
                    <circle
                      id="enqkp4e94b34"
                      r="3.828677"
                      transform="translate(0,13.290754)"
                      fill="rgb(227,230,236)"
                      fillOpacity="0.8"
                      fillRule="evenodd"
                      stroke="none"
                      strokeWidth="1"
                    />
                  </g>
                  <g id="enqkp4e94b35_to" transform="translate(19.100550,35)">
                    <circle
                      id="enqkp4e94b35"
                      r="3.828677"
                      transform="translate(-0,0)"
                      fill="rgb(227,230,236)"
                      fillOpacity="0.6"
                      fillRule="evenodd"
                      stroke="none"
                      strokeWidth="1"
                    />
                  </g>
                  <g id="enqkp4e94b36_to" transform="translate(34.999229,35)">
                    <circle
                      id="enqkp4e94b36"
                      r="3.828677"
                      transform="translate(15.899448,0)"
                      fill="rgb(227,230,236)"
                      fillOpacity="0.8"
                      fillRule="evenodd"
                      stroke="none"
                      strokeWidth="1"
                    />
                  </g>
                </g>
              </g>
            </svg>
          </div>
          <iframe
            loading={"lazy"}
            key={reloadKey}
            className={classes.iFrame}
            src={url}
          ></iframe>
        </section>
      </a.div>
    </div>
  );
}
